// Chat page helpers: formatting, attachment markup, local-only preferences and
// channel-level writes (channel edits go straight through mutate/patch/del).

import { useMemo } from 'react';
import { format, isThisYear, isToday, isYesterday } from 'date-fns';
import { create } from 'zustand';
import type { Channel, ChannelRead, Message } from '@shared/types';
import type { Op } from '@shared/sync';
import { newId } from '@shared/ids';
import { del, getDb, getMe, mutate, patch, put, useDbShallow, useMe, type Tables } from '@/lib/store';
import { toggleFavorite } from '@/lib/actions';
import { postUpload } from '@/lib/upload';
import { sanitize } from '@/components/editor/RichEditor';
import { toast } from '@/components/ui/overlays';

export const REACTIONS = ['👍', '❤️', '🎉', '😄', '👀', '🙏', '✅', '🔥'];
export const QUICK_REACTIONS = ['👍', '✅', '👀'];
export const EMOJIS = [
  '😀', '😄', '😁', '😆', '😅', '😂', '🙂', '😉',
  '😊', '😍', '🥰', '😘', '😎', '🤩', '🤔', '🤨',
  '😐', '🙄', '😏', '😴', '😬', '😢', '😭', '😤',
  '😡', '🤯', '😱', '🥳', '🤝', '👍', '👎', '👏',
  '🙌', '🙏', '💪', '👀', '🔥', '✨', '🎉', '💯',
  '✅', '❌', '⚠️', '❤️', '🚀', '📌', '💡', '☕',
];

/** Messages from the same author closer than this are shown as one run. */
export const GROUP_MS = 5 * 60_000;

// ---------------------------------------------------------------------------
// dates

export function dayLabel(ts: number) {
  if (isToday(ts)) return 'Today';
  if (isYesterday(ts)) return 'Yesterday';
  return format(ts, isThisYear(ts) ? 'EEEE, MMMM do' : 'EEEE, MMMM do, yyyy');
}

export function pillLabel(ts: number) {
  if (isToday(ts)) return 'Today';
  if (isYesterday(ts)) return 'Yesterday';
  return format(ts, isThisYear(ts) ? 'EEE, MMM d' : 'MMM d, yyyy');
}

export const timeLabel = (ts: number) => format(ts, 'h:mm a');
export const shortTime = (ts: number) => format(ts, 'h:mm');

// ---------------------------------------------------------------------------
// html helpers

export function htmlToText(html: string): string {
  if (!html) return '';
  const spaced = html.replace(/<\/(p|li|h\d|blockquote|pre)>|<br\s*\/?>/gi, ' $&');
  const doc = new DOMParser().parseFromString(spaced, 'text/html');
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

export function isBlankHtml(html: string) {
  return htmlToText(html) === '';
}

/** Drop leading/trailing empty paragraphs and trailing hard breaks (Shift+Enter). */
export function trimHtml(html: string) {
  const emptyP = '<p>(?:\\s|&nbsp;|<br\\s*/?>)*</p>';
  let h = html.trim();
  h = h.replace(new RegExp(`^(?:${emptyP})+`), '').replace(new RegExp(`(?:${emptyP})+$`), '');
  h = h.replace(/(?:<br\s*\/?>)+(<\/p>)$/i, '$1');
  return h;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

// ---------------------------------------------------------------------------
// attachments

export interface FileRef {
  url: string;
  name: string;
  size?: number;
  mime: string;
}

const EXT_MIME: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  avif: 'image/avif',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  m4a: 'audio/mp4',
  weba: 'audio/webm',
  mp4: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  pdf: 'application/pdf',
  zip: 'application/zip',
  txt: 'text/plain',
  csv: 'text/csv',
};

export const fileExt = (name: string) => (/\.([a-z0-9]+)$/i.exec(name)?.[1] ?? '').toLowerCase();
export const guessMime = (name: string) => EXT_MIME[fileExt(name)] ?? 'application/octet-stream';
export const isImage = (f: FileRef) => f.mime.startsWith('image/');
export const isAudio = (f: FileRef) => f.mime.startsWith('audio/');
export const isVideo = (f: FileRef) => f.mime.startsWith('video/');

/** Stored message markup for an uploaded file; rendered as a card by MessageBody. */
export function attachmentHtml(f: FileRef) {
  return `<p><a href="${esc(f.url)}" data-file="${esc(f.mime)}" data-size="${f.size ?? ''}">📎 ${esc(f.name)}</a></p>`;
}

export async function uploadFile(file: File): Promise<FileRef> {
  const { url, size, name } = await postUpload(file);
  return { url, size, name, mime: file.type || guessMime(file.name) };
}

export type Segment = { kind: 'html'; html: string } | { kind: 'file'; file: FileRef };

function fileFromBlock(el: Element): FileRef | null {
  if (el.tagName !== 'P') return null;
  const links = el.querySelectorAll('a');
  if (links.length !== 1) return null;
  const a = links[0];
  const href = a.getAttribute('href') ?? '';
  if (!href.startsWith('/files/')) return null;
  if ((el.textContent ?? '').trim() !== (a.textContent ?? '').trim()) return null;
  let fallback = 'file';
  try {
    fallback = decodeURIComponent(href.split('/').pop() ?? 'file').replace(/^[a-z0-9]{10}-/, '');
  } catch {
    /* keep fallback */
  }
  const name = (a.textContent ?? '').replace(/^\s*📎\s*/u, '').trim() || fallback;
  return { url: href, name, mime: a.getAttribute('data-file') || guessMime(name), size: Number(a.getAttribute('data-size')) || undefined };
}

const segCache = new Map<string, Segment[]>();

/** Sanitize a message body and split it into rich-text runs and file attachments. */
export function splitBody(html: string): Segment[] {
  const hit = segCache.get(html);
  if (hit) return hit;
  const doc = new DOMParser().parseFromString(`<div>${sanitize(html)}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  const out: Segment[] = [];
  let buf = '';
  const flush = () => {
    if (buf.trim()) out.push({ kind: 'html', html: buf });
    buf = '';
  };
  for (const node of Array.from(root?.childNodes ?? [])) {
    if (node.nodeType === 1) {
      const file = fileFromBlock(node as Element);
      if (file) {
        flush();
        out.push({ kind: 'file', file });
      } else buf += (node as Element).outerHTML;
    } else if (node.nodeType === 3) buf += esc(node.textContent ?? '');
  }
  flush();
  if (segCache.size > 500) segCache.clear();
  segCache.set(html, out);
  return out;
}

export function filesInBody(html: string): FileRef[] {
  return splitBody(html).flatMap((s) => (s.kind === 'file' ? [s.file] : []));
}

/** Every file shared in a channel (threads included), newest first. */
export function useChannelFiles(channelId: string) {
  const messages = useDbShallow((db) => Object.values(db.messages).filter((m) => m.channelId === channelId));
  return useMemo(
    () =>
      [...messages]
        .sort((a, b) => b.createdAt - a.createdAt)
        .flatMap((m) => filesInBody(m.body).map((f, i) => ({ f, m, key: `${m.id}:${i}` }))),
    [messages],
  );
}

// ---------------------------------------------------------------------------
// local-only state: starred channels (per user), lightbox, composer drafts

/** Starred channels are regular (synced, per-user) favorites. */
export function useStarredIds(): string[] {
  const me = useMe();
  return useDbShallow((db) => Object.values(db.favorites).filter((f) => f.userId === me && f.target.type === 'channel').map((f) => f.target.id));
}

export function toggleStar(channelId: string) {
  toggleFavorite({ type: 'channel', id: channelId });
}

export const useLightbox = create<{ file: FileRef | null }>(() => ({ file: null }));
export const openLightbox = (file: FileRef | null) => useLightbox.setState({ file });

/** Unsent composer text, per channel / thread, kept for the session. */
export const drafts = new Map<string, string>();

// ---------------------------------------------------------------------------
// channel data

export function dmPartner(ch: Channel, me: string) {
  return ch.memberIds.find((u) => u !== me) ?? me;
}

export function readRecord(db: Tables, me: string, channelId: string): ChannelRead | undefined {
  return Object.values(db.channelReads).find((r) => r.userId === me && r.channelId === channelId);
}

export function updateChannel(id: string, changes: Partial<Channel>) {
  mutate([patch('channels', id, changes)]);
}

export function setChannelMembers(id: string, memberIds: string[]) {
  mutate([patch('channels', id, { memberIds: [...new Set(memberIds)] })]);
}

export function joinChannel(ch: Channel) {
  const me = getMe();
  if (!ch.memberIds.includes(me)) setChannelMembers(ch.id, [...ch.memberIds, me]);
}

export function leaveChannel(ch: Channel) {
  const me = getMe();
  setChannelMembers(
    ch.id,
    ch.memberIds.filter((u) => u !== me),
  );
}

/** Delete a channel with its messages, read markers and chat notifications. */
export function deleteChannelCascade(ch: Channel) {
  const db = getDb();
  const ops: Op[] = [del('channels', ch.id)];
  for (const m of Object.values(db.messages)) if (m.channelId === ch.id) ops.push(del('messages', m.id));
  for (const r of Object.values(db.channelReads)) if (r.channelId === ch.id) ops.push(del('channelReads', r.id));
  for (const n of Object.values(db.notifications)) if (n.data?.channelId === ch.id) ops.push(del('notifications', n.id));
  mutate(ops);
}

/** Delete a message together with its thread replies. */
export function deleteMessageThread(id: string) {
  const db = getDb();
  const ops: Op[] = [del('messages', id)];
  for (const m of Object.values(db.messages)) if (m.parentId === id) ops.push(del('messages', m.id));
  mutate(ops);
}

/** Pin a message to its channel (Pinned panel), or unpin it. */
export function togglePin(msg: Message) {
  mutate([patch('messages', msg.id, msg.pinnedAt ? { pinnedAt: null, pinnedBy: null } : { pinnedAt: Date.now(), pinnedBy: getMe() })]);
}

/** Mark the whole channel unread from its newest message by someone else; false when there's none. */
export function markChannelUnread(channelId: string) {
  const me = getMe();
  let latest: Message | undefined;
  for (const m of Object.values(getDb().messages))
    if (m.channelId === channelId && !m.parentId && m.authorId !== me && (!latest || m.createdAt > latest.createdAt)) latest = m;
  if (!latest) return false;
  markUnreadFrom(latest);
  return true;
}

/** Move my read marker back so `msg` (and everything after it) counts as unread. */
export function markUnreadFrom(msg: Message) {
  const db = getDb();
  const me = getMe();
  const at = msg.createdAt - 1;
  const rec = readRecord(db, me, msg.channelId);
  mutate([
    rec
      ? patch('channelReads', rec.id, { lastReadAt: at })
      : put('channelReads', { id: newId(), userId: me, channelId: msg.channelId, lastReadAt: at, createdAt: Date.now() }),
  ]);
  return at;
}

export async function copyText(text: string, label = 'Copied to clipboard') {
  try {
    await navigator.clipboard.writeText(text);
    toast(label);
  } catch {
    toast('Could not access the clipboard');
  }
}

export function messageLink(ws: string, m: Message) {
  return `${location.origin}/${ws}/chat/${m.channelId}?m=${m.id}`;
}
